import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, ChevronLeft, ChevronRight, Gavel, RefreshCw, X } from "lucide-react";
import { banUser, getPlayerFlags, listFlags, markPlayerFlagsReviewed, rescanFlags, type FlagSource, type PlayerFlagSummary } from "@/lib/api";
import { cn, formatTimestamp } from "@/lib/utils";
import { createSubmitLock } from "@/components/player-actions/action-model";
import { parseReasons, SOURCE_LABEL } from "@/components/FlagDetails";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import { ScanCard } from "./ScanCard";
import { RulesPanel } from "./RulesPanel";
import { Findings } from "./Findings";
import "./moderation.css";

const PAGE_SIZE = 50;
const SOURCES: [string, string][] = [["", "All sources"], ["save_check", "Save check"], ["player_report", "Player report"], ["iap_duplicate", "Duplicate purchase"], ["leaderboard_submit", "Leaderboard submission"], ["full_scan", "Full scan"]];
const playerHref = (id: string) => `/accounts?player=${encodeURIComponent(id)}`;
function errorMessage(error: unknown): string { return error instanceof Error ? error.message : String(error); }

export function ModerationWorkspace() {
  const readOnly = usePreviewReadOnly();
  const [params, setParams] = useSearchParams();
  const [openPlayer, setOpenPlayer] = useState<string | null>(null);
  const [banNotice, setBanNotice] = useState<{ message: string; error: boolean } | null>(null);
  const qc = useQueryClient();
  const quickBanLock = useRef(createSubmitLock());
  const quickBan = useMutation({
    mutationFn: async ({ playerId, generation, reason }: { playerId: string; generation: number; reason: string }) => {
      await banUser(playerId, reason);
      try {
        await markPlayerFlagsReviewed(playerId, generation);
        return { message: `${playerId} permanently banned and marked reviewed.`, error: false };
      } catch (error) {
        return { message: `${playerId} was banned, but marking reviewed failed: ${errorMessage(error)}. Check the latest findings and use Mark reviewed.`, error: true };
      }
    },
    onSuccess: (notice) => {
      setBanNotice(notice);
      void qc.invalidateQueries({ queryKey: ["flags"] });
      void qc.invalidateQueries({ queryKey: ["flag-player"] });
      void qc.invalidateQueries({ queryKey: ["player-bans"] });
    },
    onError: (error) => setBanNotice({ message: `Ban failed: ${errorMessage(error)}`, error: true }),
    onSettled: () => quickBanLock.current.end(),
  });
  const rescan = useMutation({
    mutationFn: ({ playerId, generation }: { playerId: string; generation: number }) => rescanFlags(playerId, generation),
    onSuccess: (result, { playerId }) => {
      const fallback = result.fallbackSources.length
        ? ` No current snapshot for ${result.fallbackSources.map(source => SOURCE_LABEL[source as FlagSource]).join(", ")}; rechecked retained payloads.` : "";
      setBanNotice({ message: `${playerId}: rescan complete, ${result.details.length} finding${result.details.length === 1 ? "" : "s"} remain.${fallback}${result.skipped ? ` ${result.skipped} finding(s) kept because their payload is unavailable.` : ""}`,
        error: result.skipped > 0 });
    },
    onError: (error) => setBanNotice({ message: `Rescan failed: ${errorMessage(error)}. Refresh the findings before retrying.`, error: true }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["flags"] });
      void qc.invalidateQueries({ queryKey: ["flag-player"] });
    },
  });
  const source = params.get("source") ?? "";
  const reviewed = params.get("reviewed") === "true";
  const rulesTab = params.get("tab") === "rules";
  const includeBanned = params.get("includeBanned") === "true";
  const includeWhitelisted = params.get("includeWhitelisted") === "true";
  const rawOffset = Number(params.get("offset") ?? 0);
  const offset = Number.isSafeInteger(rawOffset) && rawOffset > 0 ? rawOffset : 0;
  const query = useQuery({
    queryKey: ["flags", source, reviewed, includeBanned, includeWhitelisted, offset],
    queryFn: () => listFlags({ source: (source || undefined) as FlagSource | undefined, reviewed, includeBanned, includeWhitelisted, limit: PAGE_SIZE, offset }),
    enabled: !rulesTab,
  });

  function setParam(key: string, value: string | undefined) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete("offset");
    setOpenPlayer(null);
    setParams(next, { replace: true });
  }
  function setOffset(nextOffset: number) {
    const next = new URLSearchParams(params);
    if (nextOffset <= 0) next.delete("offset"); else next.set("offset", String(nextOffset));
    setOpenPlayer(null);
    setParams(next, { replace: true });
  }
  function switchTab(tab: "unreviewed" | "reviewed" | "rules") {
    const next = new URLSearchParams(params);
    next.delete("offset"); next.delete("tab"); next.delete("reviewed");
    if (tab === "reviewed") next.set("reviewed", "true");
    if (tab === "rules") next.set("tab", "rules");
    setOpenPlayer(null); setParams(next, { replace: true });
  }

  const total = query.data?.total ?? 0;
  const rows = query.data?.rows ?? [];
  const filtered = !!(source || includeBanned || includeWhitelisted);
  const tab = rulesTab ? "rules" : reviewed ? "reviewed" : "unreviewed";

  return <WorkspaceShell crumbs={rulesTab ? [{ label: "Moderation", to: "/flags" }, { label: "Flag rules" }] : [{ label: "Moderation" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className="ws-page mo-page">
      <div className="ws-head">
        <h1>Moderation</h1>
        {!rulesTab && query.data && <span className="pw-badge">{total.toLocaleString()}</span>}
        <div className="ws-head-actions">
          <div className="ws-segmented mo-tabs" role="group" aria-label="Moderation sections">
            {([["unreviewed", "Unreviewed"], ["reviewed", "Reviewed"], ["rules", "Flag rules"]] as const).map(([value, label]) =>
              <button key={value} type="button" aria-pressed={tab === value} className={cn(tab === value && "is-active")} onClick={() => switchTab(value)}>{label}</button>)}
          </div>
        </div>
      </div>
      {rulesTab ? <RulesPanel readOnly={readOnly} /> : <>
        <ScanCard readOnly={readOnly} />
        <div className="ws-form mo-filters">
          <div className="ws-row">
            <select aria-label="Source" title="Matches retained findings for each player" value={source} onChange={e => setParam("source", e.target.value)}>
              {SOURCES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <label className={cn("mo-chip", includeBanned && "is-on")}><input type="checkbox" checked={includeBanned} onChange={e => setParam("includeBanned", e.target.checked ? "true" : undefined)} />Banned</label>
            <label className={cn("mo-chip", includeWhitelisted && "is-on")} title="Developer accounts are exempt from anti-cheat holds, not duplicate-purchase holds or bans"><input type="checkbox" checked={includeWhitelisted} onChange={e => setParam("includeWhitelisted", e.target.checked ? "true" : undefined)} />Dev accounts</label>
            {filtered && <button className="pw-button pw-button-plain" onClick={() => { setOpenPlayer(null); setParams(reviewed ? { reviewed: "true" } : {}, { replace: true }); }}><X size={13} />Clear</button>}
          </div>
        </div>
        {banNotice && <p className={cn("ws-alert", !banNotice.error && "is-ok")} role={banNotice.error ? "alert" : "status"}>{banNotice.message}</p>}
        {query.error && <p className="ws-alert" role="alert">{errorMessage(query.error)}</p>}
        <section className="ws-card is-flush">
          <div className="ws-table mo-table" role="table" aria-label="Flagged players">
            <div className="ws-table-row ws-table-head mo-row" role="row">
              {["Player", "First flagged", "Last flagged", "Count", "Latest finding", "Status"].map(label => <span role="columnheader" key={label} title={label === "Count" ? "Flag submissions, including repeats; not independent incidents" : undefined}>{label}</span>)}
              <span role="columnheader"><span className="sr-only">Actions</span></span>
            </div>
            {query.isPending ? <div className="pw-empty" role="status"><span className="pw-loading-dot" />Loading flags…</div>
              : rows.length === 0 ? <p className="ws-empty">{query.error ? "Could not load flags." : offset > 0 ? "No players on this page. Go to the previous page." : filtered ? "No players match these filters." : reviewed ? "No reviewed players." : "No flags to review."}</p>
              : rows.map(row => <FlagRow key={row.player_id} row={row} readOnly={readOnly} expanded={openPlayer === row.player_id} onToggle={() => setOpenPlayer(openPlayer === row.player_id ? null : row.player_id)}
                onReviewed={() => void qc.invalidateQueries({ queryKey: ["flags"] })} banPending={quickBan.isPending || rescan.isPending}
                banning={quickBan.isPending && quickBan.variables?.playerId === row.player_id} rescanning={rescan.isPending && rescan.variables?.playerId === row.player_id}
                onRescan={generation => { setBanNotice(null); rescan.mutate({ playerId: row.player_id, generation }); }}
                onQuickBan={generation => {
                  if (!quickBanLock.current.begin()) return;
                  setBanNotice(null);
                  quickBan.mutate({ playerId: row.player_id, generation, reason: parseReasons(row.latest_reasons).join("; ") || "Flagged for moderation" });
                }} />)}
          </div>
        </section>
        {(total > PAGE_SIZE || offset > 0) && <div className="mo-pager">
          <span className="ws-muted">{rows.length ? `${offset + 1}–${offset + rows.length} of ${total}` : `${total} players`}</span>
          <div className="ws-row">
            <button className="pw-icon-button mo-page-button" aria-label="Previous page" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}><ChevronLeft size={15} /></button>
            <button className="pw-icon-button mo-page-button" aria-label="Next page" onClick={() => setOffset(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total}><ChevronRight size={15} /></button>
          </div>
        </div>}
      </>}
    </div></div>
  </WorkspaceShell>;
}

const shortTime = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const short = (ts: number | null | undefined) => ts ? shortTime.format(ts) : "—";

function FlagRow({ row, readOnly, expanded, onToggle, onReviewed, onQuickBan, banPending, banning, onRescan, rescanning }: {
  row: PlayerFlagSummary; readOnly: boolean; expanded: boolean; onToggle: () => void; onReviewed: () => void;
  onQuickBan: (generation: number) => void; banPending: boolean; banning: boolean;
  onRescan: (generation: number) => void; rescanning: boolean;
}) {
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["flag-player", row.player_id, row.generation], queryFn: () => getPlayerFlags(row.player_id), enabled: expanded });
  const review = useMutation({
    mutationFn: (generation: number) => markPlayerFlagsReviewed(row.player_id, generation),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["flag-player", row.player_id] }); onReviewed(); },
    onError: () => {
      // A new distinct finding may have arrived since this row loaded.
      void qc.invalidateQueries({ queryKey: ["flags"] });
      void qc.invalidateQueries({ queryKey: ["flag-player", row.player_id] });
    },
  });
  const reasons = parseReasons(row.latest_reasons);
  const generation = detail.data?.summary.generation ?? row.generation;
  const disabled = readOnly || review.isPending || banPending;
  const mismatch = (detail.data?.details ?? []).some(d => parseReasons(d.reasons).some(r => r.endsWith("(history mismatch)")));
  return <div className={cn("mo-entry", expanded && "is-open")}>
    <div className="ws-table-row mo-row" role="row">
      <span role="cell" className="mo-player"><Link to={playerHref(row.player_id)} className="ws-mono" title={row.player_id}>{row.player_id}</Link></span>
      <span role="cell" className="mo-time" data-label="First" title={formatTimestamp(row.first_ts)}>{short(row.first_ts)}</span>
      <span role="cell" className="mo-time" data-label="Last" title={formatTimestamp(row.last_ts)}>{short(row.last_ts)}</span>
      <span role="cell" className="mo-count" title="Flag submissions, including repeats">{row.total_count}×</span>
      <span role="cell" className="mo-finding"><span className="pw-badge">{row.latest_source ? SOURCE_LABEL[row.latest_source] ?? row.latest_source : "Unknown"}</span>
        <span title={reasons.join("; ")}>{reasons[0] ?? "—"}{reasons.length > 1 && <em> +{reasons.length - 1}</em>}</span></span>
      <span role="cell" className="mo-status">
        <span className={cn("pw-badge", row.reviewed ? "mo-badge-ok" : "pw-badge-danger")}>{row.reviewed ? "Reviewed" : "Unreviewed"}</span>
        {row.banned && <span className="pw-badge">Banned</span>}{row.whitelisted && <span className="pw-badge">Dev</span>}
      </span>
      <span role="cell" className="mo-actions">
        <button className="pw-icon-button mo-act" onClick={() => onRescan(generation)} disabled={disabled} title="Rescan with the latest uploaded data (can't read unsynced offline progress)" aria-label={rescanning ? "Rescanning" : "Rescan"}><RefreshCw size={14} className={cn(rescanning && "mo-spin")} /></button>
        {!row.reviewed && <button className="pw-icon-button mo-act" onClick={() => review.mutate(generation)} disabled={disabled} title="Mark reviewed: allows a fresh leaderboard submission; doesn't restore a held score or remove a ban" aria-label={review.isPending ? "Marking reviewed" : "Mark reviewed"}><Check size={14} /></button>}
        {!row.banned && <button className="pw-icon-button mo-act is-danger" onClick={() => onQuickBan(generation)} disabled={disabled} title="Permanently ban and mark reviewed" aria-label={banning ? "Banning" : "Permanently ban and mark reviewed"}><Gavel size={14} /></button>}
        <button className="pw-icon-button mo-toggle" onClick={onToggle} aria-expanded={expanded} aria-label={expanded ? "Hide findings" : "Show findings"}><ChevronDown size={15} /></button>
      </span>
    </div>
    {(expanded || review.error) && <div className="mo-detail" role="row"><div role="cell">
      {review.error && <p className="ws-alert" role="alert">{errorMessage(review.error)}. Flags were refreshed; check the latest findings before retrying.</p>}
      {expanded && <p className="ws-mono mo-id">{row.player_id}</p>}
      {expanded && (detail.isPending ? <p className="ws-muted">Loading findings…</p> : detail.error ? <p className="ws-alert" role="alert">{errorMessage(detail.error)}</p> : detail.data ? <>
        {mismatch && <p className="ws-alert is-warn">History mismatches need investigation; not proof of cheating.</p>}
        <Findings rows={detail.data.details} />
      </> : null)}
    </div></div>}
  </div>;
}
